import { SiteContent } from './types';

// ─────────────────────────────────────────────────────────────
//  DEFAULT_CONTENT — the seed values baked into the build. Every
//  field renders from here unless a datastore override supersedes
//  it. Edits (via the authenticated edit mode) never touch this
//  file; they are stored as override history and merged on load.
// ─────────────────────────────────────────────────────────────

export const DEFAULT_CONTENT: SiteContent = {
  location: 'Denver',
  hero: {
    sub: 'Cellist · Educator · Studio in Denver',
    tagline:
      'Cello lessons rooted in classical tradition, attuned to each student — ' +
      'technique that serves expression, music that grows with you.',
  },

  about: {
    lede: 'A working cellist who teaches because the instrument deserves it.',
    headshot: '/images/people/berti-headshot.jpg',
    moment: '/images/people/berti-symphony-2010.jpg',
    bio1:
      'Bertis is a cellist and teacher whose musicianship was shaped in Arizona. ' +
      'She earned her place in the Arizona All-State Symphony under Dr. Temme at ' +
      'Mountain View High School, then went on to study cello performance at ' +
      'Arizona State University with Dr. Thomas Landschoot — a lineage of teachers ' +
      'whose voices still sit behind every phrase she plays.',
    pullquote:
      "The cello asks for patience, and gives back a voice you didn't know you had.",
    bio2:
      'Her playing has taken her from student recitals to the stage at Symphony Hall ' +
      'in Phoenix, where she performed with the Phoenix Symphony Guild in 2010. Now ' +
      'based in Denver, she keeps a foot in both worlds — performing when the music ' +
      'calls for it and building a private studio around the students who want to grow.',
    bio3:
      'Bertis teaches because she believes a working musician makes the best kind of ' +
      'guide: someone who still practices the hard passages, still gets nervous before ' +
      'a performance, and can show a student exactly how to work through both. She ' +
      'welcomes beginners and returning players alike — anyone ready to give the ' +
      'instrument the patience it asks for.',
  },

  mentors: {
    lede: 'The teachers whose voices still sit behind the bow.',
    list: [
      { name: 'Dr. Temme', role: 'Mountain View High School', note: 'Under whom Bertis played in the Arizona All-State Symphony.', photo: '/images/mentors/dr-temme-allstate.jpg' },
      { name: 'Dr. Thomas Landschoot', role: 'Arizona State University', note: 'Belgian cellist and Distinguished Teaching Award recipient — Bertis’s cello mentor at ASU.', photo: '/images/mentors/dr-landschoot-asu.jpg' },
    ],
  },

  listening: {
    lede: 'Public Spotify playlists, refreshed when the mood does.',
  },

  contact: {
    lede: 'For lesson inquiries, performance booking, or to say hello.',
  },

  lessons: {
    lede: 'In-studio in Denver, or online for advanced students.',
    expect:
      'Each lesson is structured around technique, repertoire, and listening. ' +
      'New students start with an interview lesson so we can find the right fit ' +
      'before committing to a schedule.',
    location:
      'Lessons are in-person at the studio in Denver. Online lessons over video ' +
      'are available for established students traveling or for advanced students ' +
      'outside the area.',
    rates: [
      { label: '30 minutes', price: '$ TBD' },
      { label: '45 minutes', price: '$ TBD' },
      { label: '60 minutes', price: '$ TBD' },
      { label: 'Interview lesson', price: 'complimentary' },
    ],
  },

  teaching: {
    lede: 'Pedagogy that prizes intent over imitation, listening over drilling.',
    cards: [
      { title: 'Technique with intent', body: 'Foundations serve expression, not the other way around. We build the hand so the music can speak.' },
      { title: 'Musicianship through listening', body: 'Score study, recordings, peer playing. The ear leads the hand, not the other way around.' },
      { title: 'Joy in the work', body: "Practice that's sustainable, repertoire that's earned. Students who finish a lesson eager for the next." },
    ],
    essay1:
      "When a new student sits down across from me, the first thing I listen for " +
      "isn't how much they can already play — it's how they hear. Our first lesson is " +
      "really a conversation: I want to know what drew you to the cello, we play a " +
      "little, and I listen for the musician who's already in there, waiting for the " +
      "technique to catch up. From there I build the plan around you. I choose " +
      "repertoire the way I'd choose it for myself — a few pieces that stretch you " +
      "paired with pieces that reward you — because a student who loves what's on the " +
      "stand is a student who practices without being asked.",
    essay2:
      "Between lessons I ask for honest, regular practice rather than a heroic scramble " +
      "the night before — fifteen focused minutes on most days will always beat two " +
      "frantic hours once a week, and I'll show you exactly what focused practice looks " +
      "like so the time truly counts. Progress comes in plateaus, and I treat them as " +
      "part of the map, not a sign that anything is wrong; when a passage won't budge we " +
      "slow down, take it apart, and rebuild it until it's yours. Performance nerves get " +
      "the same patient treatment — we rehearse the feeling, not only the notes — so that " +
      "by the time you play for anyone else, it's familiar ground. My aim for every " +
      "student is simple: to leave each lesson a little more capable, and a little more " +
      "eager for the next one.",
  },

  // Real YouTube performances — Berti can swap for her own when ready.
  // Sources verified 2026-05-20 via web search; videos may be removed
  // upstream over time, in which case the iframe shows "video unavailable".
  performances: [
    { id: 'dvorak-concerto', title: 'Dvořák · Cello Concerto in B minor', meta: 'Yo-Yo Ma · Berliner Philharmoniker · Lorin Maazel', videoId: 'ftNQzZ8NkRY' },
    { id: 'shostakovich-cello-concerto-1', title: 'Shostakovich · Cello Concerto No. 1 in E-flat, Op. 107', meta: 'Mstislav Rostropovich · Philadelphia Orchestra · Eugene Ormandy', videoId: 'cJ4USPkd9T0' },
    { id: 'rimsky-korsakov-scheherazade-kalendar', title: 'Rimsky-Korsakov · Scheherazade, II. The Story of the Kalendar Prince', meta: 'Wiener Philharmoniker · Valery Gergiev', videoId: '1ophckicO-0' },
    { id: 'mahler-symphony-5-adagietto', title: 'Mahler · Symphony No. 5, IV. Adagietto', meta: 'Berliner Philharmoniker · Sir Simon Rattle', videoId: '39OFMgh6JwY' },
    { id: 'beethoven-sonata-2', title: 'Beethoven · Cello Sonata No. 2, Op. 5', meta: 'Jacqueline du Pré · Daniel Barenboim · 1970', videoId: '0FTwwr4U-Hk' },
    { id: 'brahms-sonata-2', title: 'Brahms · Cello Sonata No. 2 in F, Op. 99', meta: 'Jacqueline du Pré · Daniel Barenboim', videoId: 'rBUtCCyn-lU' },
    { id: 'tchaikovsky-rococo', title: 'Tchaikovsky · Variations on a Rococo Theme', meta: 'Mstislav Rostropovich · Benjamin Britten', videoId: 'CsxB558NU70' },
    { id: 'shore-party', title: 'Howard Shore · A Long-Expected Party', meta: 'The Lord of the Rings · Live from Paris', videoId: '7XX0KPpkr8Y' },
  ],

  // Spotify playlists — public, linkable. Placeholder URLs for now; replace
  // with Berti's actual curated playlists.
  playlists: [
    { id: 'bertis-classics', title: 'Bertis Classics', desc: "Bertis's own curated shelf — Dvořák, Berlioz, Mahler, Saint-Saëns, Rachmaninoff.", url: 'https://open.spotify.com/playlist/24EhEcnbqYlaKL0yM9haqG', tint: 'brass', image: '/images/playlists/bertis-classics.jpg' },
    { id: 'russian-cello', title: 'Russian Cello', desc: 'Shostakovich, Prokofiev, Rachmaninoff — the dark Slavic shelf.', url: 'https://open.spotify.com/playlist/TBD-russian-cello', tint: 'deep', image: '/images/playlists/russian-cello.jpg' },
    { id: 'sonatas-suites', title: 'Sonatas & Suites', desc: 'Bach, Beethoven, Brahms — what the bookshelf rests on.', url: 'https://open.spotify.com/playlist/TBD-sonatas-suites', tint: 'warm', image: '/images/playlists/sonatas-suites.jpg' },
    { id: 'romantic-era', title: 'Romantic Era', desc: 'Brahms, Dvořák, Tchaikovsky, Elgar — the big-hearted 19th-century shelf.', url: 'https://open.spotify.com/playlist/TBD-romantic-era', tint: 'warm', image: '/images/playlists/romantic-era.jpg' },
    { id: 'soundtracks', title: 'Soundtracks I Love', desc: 'Howard Shore, John Williams, Hans Zimmer — programme music done right.', url: 'https://open.spotify.com/playlist/TBD-soundtracks', tint: 'silver', image: '/images/playlists/soundtracks.jpg' },
    { id: 'practice', title: 'Practice Inspiration', desc: 'What I put on when I want the bow to remember why it cares.', url: 'https://open.spotify.com/playlist/TBD-practice', tint: 'brass', image: '/images/playlists/practice.jpg' },
  ],

  // Editable settings. Empty → the app falls back to env (REACT_APP_CALENDLY_URL,
  // the Lambda's CONTACT_TO). Set via edit mode → these win at render / send time.
  settings: {
    calendlyUrl: '',
    contactRecipients: [],
  },
};
